import { useEffect, useMemo, useState } from 'react';
import { fetchMapEntityCountries } from '../lib/fetchMapEntities';
import { cn } from '../utils/cn';
import EntityFlagImage from './EntityFlagImage';
import { TeamLabel } from './TeamLabel';

export const EMPTY_TEAM = {
  mode: 'none',
  entityCode: null,
  name: null,
  emoji: null,
  flagUrl: null,
};

const CUSTOM_EMOJIS = [
  '⚽', '🏆', '🔴', '🔵', '⚪', '🟡', '🟢', '🟣', '⭐', '🔥', '💪', '🦁', '🦅', '🐉', '🏴', '🏳️',
];

export function getTeamDisplay(team) {
  if (!team || team.mode === 'none' || !team.name) return null;
  if (team.mode === 'custom' && team.emoji) return `${team.emoji} ${team.name}`;
  return team.name;
}

export function teamToLogFields(team) {
  if (!team || team.mode === 'none') {
    return { name: null, mode: 'none' };
  }
  return { name: team.name, mode: team.mode };
}

export default function MiracleTeamPicker({
  label,
  team,
  onChange,
  searchPlaceholder,
  customModeLabel,
  quickPickLabel,
  clearLabel,
  namePlaceholder,
  emojiLabel,
  emptyHint,
}) {
  const [entities, setEntities] = useState([]);
  const [search, setSearch] = useState('');
  const [pickerMode, setPickerMode] = useState(team.mode === 'custom' ? 'custom' : 'quick');

  useEffect(() => {
    let active = true;
    fetchMapEntityCountries().then((rows) => {
      if (active) setEntities(rows);
    });
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entities.slice(0, 80);
    return entities
      .filter(
        (e) =>
          e.name.toLowerCase().includes(q) ||
          e.entityCode.toLowerCase().includes(q)
      )
      .slice(0, 80);
  }, [entities, search]);

  const selectEntity = (entity) => {
    onChange({
      mode: 'entity',
      entityCode: entity.entityCode,
      name: entity.name,
      emoji: null,
      flagUrl: entity.flagUrl,
    });
    setSearch('');
  };

  const selectCustomEmoji = (emoji) => {
    onChange({
      ...team,
      mode: 'custom',
      entityCode: null,
      name: team.name || '',
      emoji,
      flagUrl: null,
    });
  };

  const clearTeam = () => {
    onChange({ ...EMPTY_TEAM });
    setSearch('');
  };

  const hasSelection = team.mode !== 'none' && team.name;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-slate-300">{label}</span>
        {hasSelection && (
          <button
            type="button"
            onClick={clearTeam}
            className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-300"
          >
            {clearLabel}
          </button>
        )}
      </div>

      {hasSelection ? (
        <div className="rounded-lg border border-lime-400/40 bg-lime-400/10 px-3 py-2 text-sm font-bold text-lime-300">
          <TeamLabel team={team} flagSize="md" />
        </div>
      ) : (
        <p className="text-xs text-slate-500 italic">{emptyHint}</p>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setPickerMode('quick')}
          className={cn(
            'flex-1 rounded-lg px-2 py-1.5 text-[10px] font-black uppercase tracking-wider border transition-colors',
            pickerMode === 'quick'
              ? 'bg-slate-800 border-lime-400/50 text-lime-400'
              : 'bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-600'
          )}
        >
          {quickPickLabel}
        </button>
        <button
          type="button"
          onClick={() => {
            setPickerMode('custom');
            if (team.mode !== 'custom') {
              onChange({
                mode: 'custom',
                entityCode: null,
                name: '',
                emoji: '⚽',
                flagUrl: null,
              });
            }
          }}
          className={cn(
            'flex-1 rounded-lg px-2 py-1.5 text-[10px] font-black uppercase tracking-wider border transition-colors',
            pickerMode === 'custom'
              ? 'bg-slate-800 border-lime-400/50 text-lime-400'
              : 'bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-600'
          )}
        >
          {customModeLabel}
        </button>
      </div>

      {pickerMode === 'quick' ? (
        <div className="space-y-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full bg-slate-950 border border-slate-800 rounded-lg py-2 px-3 text-sm outline-none focus:border-lime-400 transition-colors"
          />
          <div className="max-h-36 overflow-y-auto custom-scrollbar border border-slate-800 rounded-lg">
            {filtered.length === 0 ? (
              <p className="p-3 text-xs text-slate-500">—</p>
            ) : (
              filtered.map((entity) => (
                <button
                  key={entity.entityCode}
                  type="button"
                  onClick={() => selectEntity(entity)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-800 text-slate-300 transition-colors flex items-center gap-2"
                >
                  <EntityFlagImage
                    src={entity.flagUrl}
                    alt={entity.name}
                    size="sm"
                  />
                  <span>{entity.name}</span>
                </button>
              ))
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <input
            type="text"
            value={team.name || ''}
            onChange={(e) =>
              onChange({
                ...team,
                mode: 'custom',
                entityCode: null,
                name: e.target.value,
                emoji: team.emoji || '⚽',
                flagUrl: null,
              })
            }
            placeholder={namePlaceholder}
            className="w-full bg-slate-950 border border-slate-800 rounded-lg py-2 px-3 text-sm outline-none focus:border-lime-400 transition-colors"
          />
          <div>
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-2">
              {emojiLabel}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {CUSTOM_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => selectCustomEmoji(emoji)}
                  className={cn(
                    'w-9 h-9 rounded-lg border text-lg flex items-center justify-center transition-colors',
                    team.emoji === emoji
                      ? 'border-lime-400 bg-lime-400/10'
                      : 'border-slate-800 bg-slate-950 hover:border-slate-600'
                  )}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
